/**
 * PATH: packages/email-hub/src/feature/compose/fromDefaultSync.ts
 * FILE_ID: EMAIL-COMPOSE-LIB-FROMDEFAULTSYNC
 * PURPOSE: Pinned + last-used "From" mailbox defaults for the composer —
 *          synchronous cache readers, server sync, and the pure seed chooser.
 * WHY: FEAT-2026-07-03-COMPOSE-FROM-PIN — the operator asked to pin a default
 *      send-from mailbox, otherwise default to the mailbox they last sent
 *      from. Both are persisted server-side in email_preferences
 *      (default_from_account_id / last_from_account_id, /api/settings/email/)
 *      so the choice follows the user across devices; this module keeps a
 *      localStorage cache because the composer seeds its From synchronously
 *      and cannot await a fetch (same shape as undoSendSync.ts).
 * FLOW:
 *   composer mount → syncFromDefaultsFromServer() ──(throttled, TTL)──▶
 *     GET /api/settings/email/ → cache {pinned,last} in localStorage
 *   seed effect → chooseSeedFromAccount(sendable, syncedInto, pinned, last)
 *   pin toggle → setPinnedFromAccount(id|null) → cache + PUT (partial merge)
 *   successful send → recordLastFromAccountLocal(id) (server side is written
 *     by the send endpoint itself — organisation/services/email_send_preferences)
 * DEPENDENCIES:
 *   - ../lib/api (emailApiFetch): tenant/auth-aware fetch
 * RELATED:
 *   - pages/useComposePage.ts: seed effect + send-success recorder
 *   - pages/ComposePage.tsx: the pin affordance on the From select
 *   - undoSendSync.ts: the cache+TTL pattern this mirrors
 */
import { emailApiFetch } from '../lib/api'

const PINNED_KEY = 'email:compose:from-pinned'
const LAST_KEY = 'email:compose:from-last'

/** Re-fetch at most this often — the values change rarely. */
const SYNC_TTL_MS = 5 * 60 * 1000

let lastSyncAt = Number.NEGATIVE_INFINITY
let inFlight: Promise<void> | null = null

function readCachedId(key: string): number | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null || raw === '') return null
    const id = Number(raw)
    return Number.isInteger(id) && id > 0 ? id : null
  } catch {
    return null
  }
}

function writeCachedId(key: string, id: number | null): void {
  if (typeof window === 'undefined') return
  try {
    if (id === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, String(id))
  } catch {
    /* private mode / quota — cache is best-effort */
  }
}

/** The EmailAccount id the operator pinned as default From, or null. */
export function getPinnedFromAccountId(): number | null {
  return readCachedId(PINNED_KEY)
}

/** The EmailAccount id of the most recent successful send, or null. */
export function getLastFromAccountId(): number | null {
  return readCachedId(LAST_KEY)
}

/** Coerce a server value into a positive int id, or null. */
function coerceId(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * Refresh the pinned/last-used cache from the server, at most once per TTL.
 * Safe on every composer mount: coalesces concurrent calls, throttles by
 * time, never throws. `now`/`fetchImpl` are injectable for tests.
 */
export function syncFromDefaultsFromServer(
  now: number = Date.now(),
  fetchImpl: typeof emailApiFetch = emailApiFetch,
): Promise<void> {
  if (inFlight) return inFlight
  if (now - lastSyncAt < SYNC_TTL_MS) return Promise.resolve()
  lastSyncAt = now // throttle regardless of outcome so failures don't hammer
  inFlight = (async () => {
    try {
      const res = await fetchImpl('/api/settings/email/')
      if (!res.ok) return
      const data = await res.json().catch(() => null)
      const prefs = data?.preferences ?? data
      if (!prefs || typeof prefs !== 'object') return
      if ('default_from_account_id' in prefs) {
        writeCachedId(PINNED_KEY, coerceId(prefs.default_from_account_id))
      }
      if ('last_from_account_id' in prefs) {
        writeCachedId(LAST_KEY, coerceId(prefs.last_from_account_id))
      }
    } catch {
      /* offline / endpoint unavailable — keep the cached values */
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/**
 * Pin (or unpin with null) the default From mailbox. Writes the cache
 * synchronously so the very next compose honours it, then persists to the
 * server (partial-merge PUT — only this key is sent). Returns the server
 * write promise; UI may ignore it (cache already updated) or await to toast.
 */
export function setPinnedFromAccount(
  id: number | null,
  fetchImpl: typeof emailApiFetch = emailApiFetch,
): Promise<boolean> {
  writeCachedId(PINNED_KEY, id)
  return (async () => {
    try {
      const res = await fetchImpl('/api/settings/email/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: { default_from_account_id: id } }),
      })
      return res.ok
    } catch {
      return false
    }
  })()
}

/**
 * Record a successful send's mailbox in the LOCAL cache only. The server
 * copy is written by the send endpoint itself (the chokepoint), so this
 * just makes the same-device "next compose" correct without waiting for
 * the next TTL sync.
 */
export function recordLastFromAccountLocal(id: number | null | undefined): void {
  const clean = coerceId(id ?? null)
  if (clean !== null) writeCachedId(LAST_KEY, clean)
}

/**
 * Pure seed chooser for the composer's From mailbox.
 *
 * I/O CONTRACT: (sendable, syncedInto, pinnedId, lastId) -> accountId | null
 * Priority (first hit wins):
 *   1. reply context — the mailbox the original was synced INTO (answer from
 *      the address it was addressed to; a pin must not hijack replies),
 *   2. the pinned default (if still in the user's sendable list),
 *   3. the last-used mailbox (if still sendable),
 *   4. the first sendable account,
 *   5. null when nothing is sendable.
 * Stale ids (mailbox disconnected since pinning) fall through safely.
 */
export function chooseSeedFromAccount(args: {
  sendable: Array<{ id: number; email_address?: string | null }>
  /** Bare (lowercased) address the reply source was synced into, if any. */
  syncedInto?: string | null
  pinnedId: number | null
  lastId: number | null
}): number | null {
  const { sendable, syncedInto, pinnedId, lastId } = args
  if (sendable.length === 0) return null
  if (syncedInto) {
    const match = sendable.find(
      (a) => (a.email_address || '').trim().toLowerCase() === syncedInto,
    )
    if (match) return match.id
  }
  if (pinnedId !== null && sendable.some((a) => a.id === pinnedId)) return pinnedId
  if (lastId !== null && sendable.some((a) => a.id === lastId)) return lastId
  return sendable[0].id
}

/** Test-only: reset the module throttle state. */
export function __resetFromDefaultSyncForTests(): void {
  lastSyncAt = Number.NEGATIVE_INFINITY
  inFlight = null
}
